import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, LogOut, Briefcase, CheckCircle, MapPin, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import NotificationBell, { refreshNotifications } from '../components/NotificationBell';
import Logo from '../components/Logo';
import ReferralCard from '../components/ReferralCard';
import VerificationCard from '../components/VerificationCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RunnerDashboard({ user }) {
  const navigate = useNavigate();
  const [availableErrands, setAvailableErrands] = useState([]);
  const [myErrands, setMyErrands] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_ratings: 0, completed_jobs: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [avail, my, s] = await Promise.all([
        fetch(`${BACKEND_URL}/api/errands/available`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/errands/my`, { credentials: 'include' }),
        user?.user_id ? fetch(`${BACKEND_URL}/api/runners/${user.user_id}/stats`) : Promise.resolve(null)
      ]);
      if (avail.ok) setAvailableErrands(await avail.json());
      if (my.ok) setMyErrands(await my.json());
      if (s && s.ok) setStats(await s.json());
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/errands/${id}/accept`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to accept');
      toast.success('Errand accepted!');
      fetchData();
      refreshNotifications();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdate = async (id, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/errands/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Status updated!');
      fetchData();
      refreshNotifications();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLogout = async () => {
    await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  const totalEarnings = myErrands.filter(e => e.status === 'completed').reduce((s, e) => s + e.price, 0);

  const renderStops = (errand) => {
    if (!errand.stops_data || errand.stops_data.length === 0) return null;
    return (
      <div className="mb-3 space-y-1 bg-slate-50 rounded-lg p-2">
        {errand.stops_data.map((s, i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            <MapPin className="w-3 h-3 text-teal-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 font-medium truncate">{s.address}</p>
              {s.notes && <p className="text-slate-500 truncate">{s.notes}</p>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" showText />
            <div className="flex items-center gap-3">
              <NotificationBell />
              <span data-testid="user-name" className="text-slate-700 font-medium hidden md:block">{user?.name}</span>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full px-4 py-2 font-medium btn-hover flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 lg:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-available" className="bg-white border border-slate-100 shadow-sm rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 w-10 h-10 rounded-full flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-slate-600">Available</p>
                <p className="text-xl font-bold text-slate-900">{availableErrands.length}</p>
              </div>
            </div>
          </Card>
          <Card data-testid="stat-active" className="bg-white border border-slate-100 shadow-sm rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-slate-600">Active</p>
                <p className="text-xl font-bold text-slate-900">{myErrands.filter(e => ['accepted', 'in_progress'].includes(e.status)).length}</p>
              </div>
            </div>
          </Card>
          <Card data-testid="stat-rating" className="bg-white border border-slate-100 shadow-sm rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 w-10 h-10 rounded-full flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600 fill-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-slate-600">Rating</p>
                <p className="text-xl font-bold text-slate-900">
                  {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '—'}
                  <span className="text-xs text-slate-500 font-normal ml-1">({stats.total_ratings})</span>
                </p>
              </div>
            </div>
          </Card>
          <Card data-testid="stat-earnings" className="bg-white border border-slate-100 shadow-sm rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-teal-700" />
              </div>
              <div>
                <p className="text-xs text-slate-600">Earnings</p>
                <p className="text-xl font-bold text-teal-700">R{totalEarnings.toFixed(0)}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <VerificationCard />
          <ReferralCard />
        </div>

        <Tabs defaultValue="available" className="w-full">
          <TabsList className="grid w-full md:w-96 grid-cols-2 mb-8">
            <TabsTrigger data-testid="tab-available" value="available">Available Jobs</TabsTrigger>
            <TabsTrigger data-testid="tab-my-jobs" value="my-jobs">My Jobs</TabsTrigger>
          </TabsList>

          <TabsContent value="available">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading jobs...</p>
              </div>
            ) : availableErrands.length === 0 ? (
              <Card data-testid="no-available-message" className="bg-white border border-slate-100 shadow-sm rounded-xl p-12 text-center">
                <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">No available jobs</h3>
                <p className="text-slate-600">Check back later for new opportunities</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableErrands.map((errand) => (
                  <Card key={errand.errand_id} data-testid={`available-errand-${errand.errand_id}`} className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 card-hover">
                    <div className="flex items-start gap-3 mb-3">
                      {errand.errand_type === 'parcel' ? (
                        <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-teal-700" />
                        </div>
                      ) : (
                        <div className="bg-orange-100 w-10 h-10 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-orange-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-bold text-slate-900">{errand.errand_type === 'parcel' ? 'Parcel Delivery' : 'Queue Assistance'}</p>
                        <p className="text-sm text-slate-600">{errand.stops} {errand.stops === 1 ? 'stop' : 'stops'}</p>
                      </div>
                    </div>
                    {renderStops(errand)}
                    <p className="text-slate-700 text-sm mb-3 line-clamp-2">{errand.description}</p>
                    <p className="text-xs text-slate-500 mb-3">Customer: {errand.customer_name}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-bold text-teal-700">R{errand.price.toFixed(2)}</p>
                      <Button
                        data-testid={`accept-btn-${errand.errand_id}`}
                        onClick={() => handleAccept(errand.errand_id)}
                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 py-2 font-medium shadow-md btn-hover"
                      >
                        Accept
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-jobs">
            {myErrands.length === 0 ? (
              <Card data-testid="no-my-jobs-message" className="bg-white border border-slate-100 shadow-sm rounded-xl p-12 text-center">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">No accepted jobs</h3>
                <p className="text-slate-600">Accept jobs from the available list to get started</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myErrands.map((errand) => (
                  <Card key={errand.errand_id} data-testid={`my-errand-${errand.errand_id}`} className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-3">
                      {errand.errand_type === 'parcel' ? (
                        <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-teal-700" />
                        </div>
                      ) : (
                        <div className="bg-orange-100 w-10 h-10 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-orange-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-bold text-slate-900">{errand.errand_type === 'parcel' ? 'Parcel Delivery' : 'Queue Assistance'}</p>
                        <p className="text-sm text-slate-600">{errand.stops} {errand.stops === 1 ? 'stop' : 'stops'}</p>
                      </div>
                    </div>
                    {renderStops(errand)}
                    <p className="text-slate-700 text-sm mb-3 line-clamp-2">{errand.description}</p>
                    <p className="text-xs text-slate-500 mb-2">Customer: {errand.customer_name}</p>
                    <p className="text-xl font-bold text-teal-700 mb-4">R{errand.price.toFixed(2)}</p>

                    {errand.status === 'accepted' && (
                      <Button
                        data-testid={`start-btn-${errand.errand_id}`}
                        onClick={() => handleUpdate(errand.errand_id, 'in_progress')}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full py-2 font-medium btn-hover"
                      >
                        Start Job
                      </Button>
                    )}
                    {errand.status === 'in_progress' && (
                      <Button
                        data-testid={`complete-btn-${errand.errand_id}`}
                        onClick={() => handleUpdate(errand.errand_id, 'completed')}
                        className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-2 font-medium btn-hover"
                      >
                        Complete Job
                      </Button>
                    )}
                    {errand.status === 'completed' && (
                      <div className="text-center py-2 bg-teal-50 rounded-full text-teal-700 font-medium text-sm">
                        ✓ Completed
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
